import { navigate } from "./app.js";
import { esc, siteLocationLabel, parseCoordinate, coordinateInRange } from "./utils.js";
import { rememberLocation } from "./lastLocation.js";

/**
 * Dive sites on a map.
 *
 * Leaflet comes from the CDN as a global, the same way Bootstrap does,
 * rather than being bundled: it is ~150KB that only the people who open
 * the Map tab need, and keeping it out of app.js means the other pages
 * do not pay for it. The cost is that `L` may be missing — so every
 * entry point here checks before using it and says so plainly instead
 * of throwing into a blank panel.
 */

const TILE_URL = "https://osm-proxy.divecohorts.com/{z}/{x}/{y}.png";
// Required by the ODbL. The proxy is ours; the data is not.
const ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Where to look when we have no idea where the reader is. A bounded
// region, not the globe: a world view turns every site into a numbered
// cluster badge on a map two inches across, which reads as "no dive
// sites" rather than "all of them".
//
// North America because that is where this app's divers are. A reader
// elsewhere lands here and pans, and the viewport fetch follows them.
// Deliberately stops at 55°N rather than reaching the Arctic. Mercator
// stretches high latitudes badly: a box topping out at 72° is so tall
// that fitting it forces the zoom down until 330° of longitude is on
// screen — a world map again, which is the thing this constant exists
// to avoid. 55° covers where people actually dive and keeps the frame
// tight. Hawaii is included because it costs nothing: latitude is what
// limits the zoom here, not width.
const NORTH_AMERICA = [
  [15.0, -161.0], // south-west: southern Mexico, out to Hawaii
  [55.0, -60.0],  // north-east: northern Canada, out to Newfoundland
];

/** How much map to show around a known position. Twenty miles is the
 *  radius, so the window is forty across — a morning's drive to a dive
 *  site, rather than a continent or a street. */
const NEARBY_RADIUS_KM = 32.19;
const MAX_ZOOM = 18;
/** One site, or several in the same bay, should not zoom to street level. */
const MAX_FIT_ZOOM = 12;
/** How long after the last pan or zoom before we treat the view as
 *  chosen. Long enough to ride out a drag, short enough not to feel
 *  laggy when the pins are what you are waiting for. */
const VIEW_SETTLE_MS = 400;

/** A box of `radiusKm` around a point.
 *
 * Returned as bounds rather than a zoom level on purpose: the zoom that
 * shows twenty miles depends on how wide the map is and how far north
 * you are, and fitBounds works that out from the container. Picking a
 * number would be right on one screen and wrong on every other. */
function boundsAround(lat, lng, radiusKm) {
  const latDelta = radiusKm / 111.32;
  // A degree of longitude narrows towards the poles; without the cosine
  // the box is far too wide in British Columbia and far too narrow near
  // the equator. Floored so a position at the pole cannot divide by ~0.
  const lngDelta = radiusKm / Math.max(111.32 * Math.cos((lat * Math.PI) / 180), 1e-6);
  return [
    [lat - latDelta, lng - lngDelta],
    [lat + latDelta, lng + lngDelta],
  ];
}

/**
 * Leaflet's viewport as a bbox the API will accept, or null for "do not
 * narrow at all".
 *
 * getBounds() reports the world as drawn, not as it exists. Zoom out far
 * enough and the map shows several copies side by side, so the longitudes
 * run past ±180 — a real example from the console was
 * -1033.59 … 786.09, slightly over five worlds wide. The API rejects
 * that, correctly, so the clamping belongs here.
 *
 * Three cases:
 *   - a span of 360° or more: everything is visible, so there is nothing
 *     to narrow to. Returns null and the caller omits the parameter.
 *   - a span that crosses the antimeridian once wrapped: returned with
 *     west greater than east, which the endpoint understands.
 *   - anything else: wrapped into range unchanged in meaning.
 *
 * Latitudes are clamped rather than wrapped; the projection cannot show
 * past the poles, and a map tilted over the top is not a thing.
 */
export function normalisedBBox(bboxString) {
  if (!bboxString) return null;
  const parts = String(bboxString).split(",").map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;

  const [west, south, east, north] = parts;
  if (east - west >= 360) return null;

  const wrap = (lng) => (((lng + 180) % 360) + 360) % 360 - 180;
  const clamp = (lat) => Math.min(90, Math.max(-90, lat));

  const w = wrap(west);
  const e = wrap(east);
  const sLat = clamp(Math.min(south, north));
  const nLat = clamp(Math.max(south, north));
  return `${w},${sLat},${e},${nLat}`;
}

/** The longitude of `lng` on whichever copy of the world is nearest to
 *  `nearLng`.
 *
 * Zoom out and the map draws the world several times over; pan west past
 * 180° and the viewport is on the next copy along. A marker is drawn at
 * its own longitude, which is on the *first* copy — so panning across
 * the dateline left Fiji behind even though the request for it had
 * succeeded and returned the sites. Shifting each pin by whole worlds
 * puts it on the copy the reader is actually looking at.
 *
 * Whole multiples of 360 only, so the position on the ground is
 * unchanged — this moves which drawing of the world the pin belongs to,
 * not where it is. */
function onNearestWorldCopy(lng, nearLng) {
  if (!Number.isFinite(nearLng)) return lng;
  return lng + 360 * Math.round((nearLng - lng) / 360);
}

export function leafletAvailable() {
  return typeof L !== "undefined" && L != null;
}

/** What hovering a pin shows.
 *
 * A tooltip rather than a popup, because clicking a pin navigates
 * straight to the site — a popup would put a second click between the
 * pin and the page it stands for. On touch there is no hover and the
 * tap just navigates, which is the behaviour asked for.
 */
function tooltipHtml(site) {
  const location = siteLocationLabel(site);
  const depth = site.max_depth_m
    ? `<span class="text-slate ms-2"><i class="bi bi-water me-1"></i>${site.max_depth_m}m</span>`
    : "";
  return `
    <span class="dc-map-tip">
      <span class="fw-semibold">${esc(site.name)}</span>${depth}
      ${location ? `<span class="d-block text-slate">${esc(location)}</span>` : ""}
    </span>`;
}

/**
 * Draws `sites` on `container`.
 *
 * Returns a handle with destroy(), because Leaflet keeps listeners on
 * window and a map left behind on a container that has been replaced
 * goes on responding to resizes forever.
 */
export function renderSiteMap(
  container,
  sites,
  {
    onOpen = (id) => navigate(`/dive-site/${id}`),
    /** Called with a bbox string after the reader settles on a view.
     *  The caller refetches; this module does not know about the API. */
    onViewChange = null,
    /** Where to open. When given, it wins over fitting to the sites:
     *  coming back to the map should put the reader where they left it,
     *  not re-frame the whole directory around them. */
    restoreView = null,
  } = {}
) {
  if (!leafletAvailable()) {
    container.innerHTML = `<div class="dc-empty"><i class="bi bi-map"></i><div class="fw-semibold">The map could not load</div><div>Check your connection and reload the page.</div></div>`;
    return { destroy() {} };
  }

  // Declared up front because replaceMarkers() below keeps its count in
  // step, and it is returned at the end.
  const handle = { markerCount: 0 };

  const map = L.map(container, { scrollWheelZoom: true });
  // Measure before framing anything.
  //
  // The map is built the instant its panel stops being display:none, so
  // Leaflet can read the container as zero-sized. Every fitBounds below
  // is then computed against nothing and falls back to the minimum
  // zoom: asking for North America produced a view spanning 330° of
  // longitude, centred over Asia, with no dive sites in it. Calling
  // invalidateSize here forces the measurement first, so the fit is
  // against the real container.
  map.invalidateSize?.();
  L.tileLayer(TILE_URL, { maxZoom: MAX_ZOOM, attribution: ATTRIBUTION }).addTo(map);

  // A site with no usable coordinates cannot be placed. The columns are
  // NOT NULL server-side, so this is defence rather than expectation —
  // but one bad row must not take the whole map down.
  //
  // Number(null) and Number("") are both 0, so a plain Number.isFinite
  // check accepts a missing coordinate and pins the site at 0,0 — a spot
  // in the Gulf of Guinea, plausible enough on a zoomed-out map to be
  // mistaken for real data. Hence the explicit empty check, and the
  // range check after it.
  const placeableOf = (list) =>
    list.filter((s) => coordinateInRange(s.latitude, 90) && coordinateInRange(s.longitude, 180));

  function buildMarker(site, nearLng) {
    const marker = L.marker([
      parseCoordinate(site.latitude),
      onNearestWorldCopy(parseCoordinate(site.longitude), nearLng),
    ], {
      title: site.name,
      alt: site.name,
    });
    marker.bindTooltip(tooltipHtml(site), { direction: "top", offset: [0, -12] });
    marker.on("click", () => onOpen(site.id));
    // Reachable without a mouse: Leaflet markers are focusable, and
    // Enter on a focused pin should do what clicking it does.
    marker.on("keypress", (e) => {
      if (e.originalEvent?.key === "Enter") onOpen(site.id);
    });
    return marker;
  }

  // Clustered when the plugin is there, plain otherwise. A few hundred
  // pins are fine unclustered; several thousand would not be, and the
  // fallback means a missing plugin degrades instead of breaking.
  const clustered = typeof L.markerClusterGroup === "function";
  const layer = clustered
    ? L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 45 })
    : L.layerGroup([]);

  let placeable = placeableOf(sites);
  let markers = [];

  /** Swaps the pins without touching the view.
   *
   *  clearLayers() rather than rebuilding the group: the layer is
   *  already on the map, and replacing it would make the pins blink on
   *  every viewport fetch — which happens while the reader is panning. */
  function replaceMarkers(next) {
    placeable = placeableOf(next);
    // Taken once per rebuild. Markers are replaced on every viewport
    // change, so they follow the reader across copies of the world.
    const nearLng = map.getCenter?.()?.lng;
    markers = placeable.map((site) => buildMarker(site, nearLng));
    layer.clearLayers?.();
    markers.forEach((m) => layer.addLayer(m));
    handle.markerCount = markers.length;
  }

  /** Frames the map on twenty miles of a known position. */
  function showNearby(lat, lng) {
    map.fitBounds(L.latLngBounds(boundsAround(lat, lng, NEARBY_RADIUS_KM)), {
      padding: [20, 20],
      maxZoom: MAX_FIT_ZOOM,
    });
  }

  // Three ways to open, most specific first.
  //
  //   1. a view the reader chose and left        — put them back in it
  //   2. a position we know they are at          — twenty miles of it
  //   3. nothing                                 — North America
  //
  // Deliberately not "fit to the sites that came back". That framed the
  // whole globe whenever the directory spanned continents, which is the
  // bug that made the map look empty.
  //
  // Before the pins, not after: placing a marker decides which copy of
  // the world it belongs on, and that is measured from the map centre.
  // With the markers first, the centre is still the default (0°) and
  // every pin lands on the wrong copy for a view out past the dateline
  // — sites in Fiji were drawn a world away from a reader looking
  // straight at Fiji.
  if (restoreView && restoreView.source === "map" && restoreView.zoom != null) {
    map.setView([restoreView.lat, restoreView.lng], restoreView.zoom);
  } else if (restoreView) {
    // A device fix is a position, not a view: it carries no zoom, so it
    // gets the standard window rather than a remembered one.
    showNearby(restoreView.lat, restoreView.lng);
  } else {
    map.fitBounds(L.latLngBounds(NORTH_AMERICA), { padding: [10, 10] });
  }

  layer.addTo(map);
  replaceMarkers(sites);

  // Panning and zooming are the reader saying where they care about.
  // Debounced, because Leaflet fires moveend on every flick of a drag
  // and each one would otherwise be a request.
  let settleHandle;
  function onMoved() {
    clearTimeout(settleHandle);
    settleHandle = setTimeout(() => {
      const centre = map.getCenter?.();
      if (centre) {
        rememberLocation({ lat: centre.lat, lng: centre.lng, zoom: map.getZoom?.(), source: "map" });
      }
      const bounds = map.getBounds?.();
      if (onViewChange && bounds?.toBBoxString) {
        onViewChange(normalisedBBox(bounds.toBBoxString()));
      }
    }, VIEW_SETTLE_MS);
  }
  map.on?.("moveend", onMoved);
  map.on?.("zoomend", onMoved);

  return Object.assign(handle, {
    map,
    /** Leaflet needs telling when its container changes size — a map
     *  built inside a hidden panel measures zero and draws one tile. */
    refresh() {
      map.invalidateSize();
    },
    /** The box the map is currently showing, for a first fetch. Null
     *  when the whole world is in view and there is nothing to narrow. */
    currentBBox() {
      return normalisedBBox(map.getBounds?.()?.toBBoxString?.());
    },
    /** Re-frames on a position — used when a geolocation fix arrives
     *  after the map is already open. */
    showNearby,
    /** Replaces the pins without disturbing the view — used when a
     *  viewport fetch comes back, so the map does not jump under the
     *  hand that is panning it. */
    setSites(next) {
      replaceMarkers(next);
    },
    destroy() {
      clearTimeout(settleHandle);
      map.remove();
    },
  });
}
